/** Session-scoped dismiss for the "finish setting up your profile" toast. */
const PROFILE_SETUP_BANNER_DISMISSED_KEY = 'profileSetupBannerDismissed';

export const PROFILE_SETUP_HIGHLIGHT_STATE_KEY = 'highlightProfileSetup';

export type ProfileSetupLocationState = {
    [PROFILE_SETUP_HIGHLIGHT_STATE_KEY]?: boolean;
};

export type ProfileSetupMissingItemId =
    | 'profilePicture'
    | 'profilePictureInvalid';

export type ProfileSetupMissingItem = {
    id: ProfileSetupMissingItemId;
    label: string;
};

/** Roles that must have a (valid) profile picture — same gate as CustomAppBar. */
export function isProfilePictureRequiredRole(
    role: string | undefined,
): boolean {
    const roleStr = String(role ?? '').toLowerCase();
    return (
        (roleStr.includes('guest') ||
            roleStr.includes('external_attendee_user') ||
            roleStr.includes('contractor')) &&
        roleStr !== 'guest_company'
    );
}

type ProfilePictureLike = {
    id?: string;
    src?: string;
    valid?: boolean;
} | null;

/**
 * Mandatory profile-setup gaps only (excludes optional fields such as vehicle plate).
 */
export function getMandatoryProfileSetupMissingItems(
    profileDetails:
        | {
              profilePicture?: ProfilePictureLike;
          }
        | null
        | undefined,
    role: string | undefined,
): ProfileSetupMissingItem[] {
    if (!isProfilePictureRequiredRole(role)) return [];

    const picture = profileDetails?.profilePicture;
    if (!picture) {
        return [{ id: 'profilePicture', label: 'Profile picture' }];
    }
    // Mirror CustomAppBar: any non-true `valid` (false or missing) needs a clearer photo
    if (!picture.valid) {
        return [
            {
                id: 'profilePictureInvalid',
                label: 'Profile picture',
            },
        ];
    }
    return [];
}

export function isProfileSetupBannerDismissed(): boolean {
    try {
        return (
            sessionStorage.getItem(PROFILE_SETUP_BANNER_DISMISSED_KEY) === '1'
        );
    } catch {
        return false;
    }
}

export function dismissProfileSetupBanner(): void {
    try {
        sessionStorage.setItem(PROFILE_SETUP_BANNER_DISMISSED_KEY, '1');
    } catch {
        // ignore storage failures
    }
}

export function clearProfileSetupBannerDismissed(): void {
    try {
        sessionStorage.removeItem(PROFILE_SETUP_BANNER_DISMISSED_KEY);
    } catch {
        // ignore storage failures
    }
}
